import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, BookOpen, Layers } from 'lucide-react';

interface Story {
  role?: string;
  goal?: string;
  benefit?: string;
  acceptance_criteria?: string[];
}

interface Epic {
  title?: string;
  description?: string;
  stories?: Story[];
}

interface RequirementsDoc {
  title?: string;
  summary?: string;
  epics?: Epic[];
  [key: string]: unknown;
}

interface Props {
  data: RequirementsDoc;
}

export function RequirementsViewer({ data }: Props) {
  const { title, summary, epics = [] } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-display font-bold mb-2">{title || 'Requirements Document'}</h2>
        {summary && <p className="text-muted-foreground">{summary}</p>}
      </div>

      {/* Epics */}
      {epics.map((epic, ei) => (
        <Card key={ei} className="border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Layers className="w-5 h-5 text-primary" />
              {epic.title || `Epic ${ei + 1}`}
            </CardTitle>
            {epic.description && (
              <p className="text-sm text-muted-foreground">{epic.description}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {epic.stories?.map((story, si) => (
              <div key={si} className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
                {/* User story sentence */}
                <div className="flex items-start gap-2">
                  <BookOpen className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <p className="text-sm">
                    {story.role && <span>As a <strong>{story.role}</strong>, </span>}
                    {story.goal && <span>I want to <strong>{story.goal}</strong> </span>}
                    {story.benefit && <span>so that <strong>{story.benefit}</strong></span>}
                  </p>
                </div>

                {/* Acceptance criteria */}
                {story.acceptance_criteria && story.acceptance_criteria.length > 0 && (
                  <div className="pl-6">
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">
                      Acceptance Criteria
                    </p>
                    <ul className="space-y-1">
                      {story.acceptance_criteria.map((ac, ai) => (
                        <li key={ai} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="w-3.5 h-3.5 text-reqqy-success mt-0.5 shrink-0" />
                          <span>{ac}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
            {(!epic.stories || epic.stories.length === 0) && (
              <p className="text-sm text-muted-foreground">No stories in this epic</p>
            )}
          </CardContent>
        </Card>
      ))}

      {epics.length === 0 && (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">No epics found in this document.</p>
            <pre className="mt-3 bg-muted/50 rounded p-3 text-xs overflow-auto max-h-64 font-mono">
              {JSON.stringify(data, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
